import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';

const AudioPlayer = ({ title, category, duration, description, audioUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [audioDuration, setAudioDuration] = useState(duration || '0:00');
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        const progressPercent = (audio.currentTime / audio.duration) * 100;
        setProgress(progressPercent);
        
        const mins = Math.floor(audio.currentTime / 60);
        const secs = Math.floor(audio.currentTime % 60);
        setCurrentTime(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
    };

    const handleLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        const mins = Math.floor(audio.duration / 60);
        const secs = Math.floor(audio.duration % 60);
        setAudioDuration(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime('0:00');
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      // Pause all other audio elements on the page
      document.querySelectorAll('audio').forEach(a => {
        if (a !== audio) {
          a.pause();
        }
      });
      audio.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    audio.currentTime = clickPosition * audio.duration;
    setProgress(clickPosition * 100);
  };

  return (
    <div className="bg-[#161616] border border-[#2A2A2A] rounded-xl p-5 hover:border-[#C9A962]/30 transition-all duration-300 group">
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}
      
      <div className="flex items-start justify-between mb-4">
        <div>
          <span className="text-[#C9A962] text-xs font-medium uppercase tracking-wider">
            {category}
          </span>
          <h4 className="text-white font-medium mt-1">{title}</h4>
          {description && (
            <p className="text-[#707070] text-sm mt-1">{description}</p>
          )}
        </div>
        <Volume2 size={18} className="text-[#505050] group-hover:text-[#C9A962] transition-colors" />
      </div>

      <div className="flex items-center gap-4">
        {/* Play Button */}
        <button
          onClick={togglePlay}
          disabled={!audioUrl}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
            isPlaying
              ? 'bg-[#C9A962] text-[#0D0D0D]'
              : 'bg-[#2A2A2A] text-white hover:bg-[#C9A962] hover:text-[#0D0D0D]'
          } ${!audioUrl ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>

        {/* Progress Bar */}
        <div className="flex-1">
          <div
            className="h-1.5 bg-[#2A2A2A] rounded-full cursor-pointer overflow-hidden"
            onClick={handleProgressClick}
          >
            <div
              className="h-full bg-[#C9A962] rounded-full transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Time Display */}
        <div className="text-[#707070] text-xs font-mono min-w-[70px] text-right">
          {currentTime} / {audioDuration}
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
